import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Loader2, Plus, Wrench, Clock, CheckCircle2, AlertCircle,
  Search, FileDown, Eye, Download, FileText, Trash2, Bell,
  IndianRupee, ClipboardCheck, TrendingUp,
} from 'lucide-react';

const RNM_TYPES = ['Repair', 'Servicing', 'Insurance'];
const REPAIR_CATEGORIES = ['Engine', 'Clutch', 'Suspension', 'Electrical', 'AC', 'Body Work', 'Other'];
const SERVICE_TYPES = ['Regular', 'Major'];
const SERVICE_ITEMS = ['Engine Oil', 'Oil Filter', 'Air Filter', 'Brake Pads', 'Coolant', 'Other'];
const INSURANCE_MODES = ['Cash', 'Claim'];
const STATUSES = ['Pending', 'In Progress', 'Completed'];

const todayStr = () => new Date().toISOString().split('T')[0];

const statusStyles = {
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const typeStyles = {
  Repair: 'bg-rose-50 text-rose-700 border-rose-200',
  Servicing: 'bg-sky-50 text-sky-700 border-sky-200',
  Insurance: 'bg-violet-50 text-violet-700 border-violet-200',
};

const fmtInr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusStyles[status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}
    data-testid={`rm-status-${status?.toLowerCase().replace(' ', '-')}`}
  >
    {status === 'Completed' && <CheckCircle2 className="w-3 h-3" />}
    {status === 'In Progress' && <Clock className="w-3 h-3" />}
    {status === 'Pending' && <AlertCircle className="w-3 h-3" />}
    {status}
  </span>
);

const TypeBadge = ({ type }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${typeStyles[type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
    {type || '—'}
  </span>
);

const StatCard = ({ icon: Icon, label, value, sub, tone = 'slate', testid }) => {
  const tones = {
    slate: 'from-slate-50 to-white text-slate-700',
    amber: 'from-amber-50 to-white text-amber-800',
    emerald: 'from-emerald-50 to-white text-emerald-800',
    blue: 'from-blue-50 to-white text-blue-800',
    violet: 'from-violet-50 to-white text-violet-800',
    rose: 'from-rose-50 to-white text-rose-800',
    sky: 'from-sky-50 to-white text-sky-800',
  };
  return (
    <Card className={`bg-gradient-to-br ${tones[tone]} border`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          {Icon && <Icon className="w-5 h-5 opacity-70" />}
        </div>
      </CardContent>
    </Card>
  );
};

const RepairMaintenanceModule = () => {
  const { API, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState('dashboard');
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total: 0, active: 0, pending: 0, in_progress: 0, completed: 0,
    total_spend: 0, avg_downtime_days: 0,
    by_type: { Repair: 0, Servicing: 0, Insurance: 0 },
    cars_in_workshop: [], cars_in_workshop_count: 0,
    oldest_pending: null,
  });
  const [analytics, setAnalytics] = useState({ vehicles: [] });
  const [alerts, setAlerts] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);        // full record when editing
  const [form, setForm] = useState(null);                // editable model
  const [billFile, setBillFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    car_number: '', type: '', km_reading: '', workshop_name: '', date_of_entry: todayStr(),
  });

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();

      const [recRes, vehRes, sumRes, anaRes, aleRes] = await Promise.all([
        axios.get(`${API}/rm-records`, { params }),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/rm-records/summary/stats`),
        axios.get(`${API}/rm-records/analytics/by-vehicle`),
        axios.get(`${API}/rm-records/alerts/list`),
      ]);
      let recs = recRes.data;
      if (filterType !== 'all') {
        recs = recs.filter(r => (r.type || '').toLowerCase() === filterType.toLowerCase());
      }
      setRecords(recs);
      setVehicles(vehRes.data);
      setSummary(sumRes.data);
      setAnalytics(anaRes.data);
      setAlerts(aleRes.data?.alerts || []);
    } catch (err) {
      toast.error('Failed to load R&M records');
    } finally {
      setLoading(false);
    }
  }, [API, filterStatus, filterType, dateFrom, dateTo, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const vehicleOptions = useMemo(() => vehicles.map(v => v.vehicle_number).sort(), [vehicles]);

  const openForm = (record) => {
    setSelected(record);
    setForm({
      ...record,
      type: record.type || '',
      km_reading: record.km_reading ?? '',
      labour_cost: record.labour_cost ?? '',
      parts_cost: record.parts_cost ?? '',
      approved_amount: record.approved_amount ?? '',
      items_changed: record.items_changed || [],
      parts_line_items: (record.parts_line_items && record.parts_line_items.length)
        ? record.parts_line_items
        : [{ part_number: '', name: '', quantity: 1, unit_cost: 0 }],
      notes: record.notes || record.description || '',
    });
    setBillFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setSelected(null);
    setForm(null);
    setBillFile(null);
  };

  const handleCreate = async () => {
    if (!createForm.car_number) return toast.error('Car Number is required');
    if (createForm.km_reading === '' || createForm.km_reading === null) return toast.error('KM Reading is required');
    setSubmitting(true);
    try {
      const matched = vehicles.find(v => v.vehicle_number.toUpperCase() === createForm.car_number.toUpperCase());
      const payload = {
        car_number: createForm.car_number.toUpperCase().trim(),
        vehicle_id: matched?.id || '',
        type: createForm.type || null,
        km_reading: parseInt(createForm.km_reading, 10),
        workshop_name: createForm.workshop_name,
        date_of_entry: createForm.date_of_entry || todayStr(),
        status: 'Pending',
      };
      const { data } = await axios.post(`${API}/rm-records`, payload);
      toast.success(`Created ${data.rnm_id || data.rm_record_id}`);
      setCreateOpen(false);
      setCreateForm({ car_number: '', type: '', km_reading: '', workshop_name: '', date_of_entry: todayStr() });
      await fetchAll();
      openForm(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!form) return;
    if (form.km_reading === '' || form.km_reading === null || form.km_reading === undefined) {
      return toast.error('KM Reading is required');
    }
    // Filter out empty line items and compute parts_cost
    const cleanLineItems = (form.parts_line_items || [])
      .map(it => ({
        part_number: (it.part_number || '').trim(),
        name: (it.name || '').trim(),
        quantity: parseFloat(it.quantity) || 0,
        unit_cost: parseFloat(it.unit_cost) || 0,
      }))
      .filter(it => it.part_number || it.name || it.quantity || it.unit_cost);
    const partsCostFromItems = cleanLineItems.reduce(
      (sum, it) => sum + (it.quantity * it.unit_cost), 0
    );

    setSubmitting(true);
    try {
      const payload = {
        workshop_name: form.workshop_name || '',
        type: form.type || null,
        km_reading: parseInt(form.km_reading, 10),
        expected_completion_date: form.expected_completion_date || null,
        completion_date: form.completion_date || null,
        status: form.status || 'Pending',
        notes: form.notes || '',
        description: form.description || form.notes || '',
      };
      if (form.type === 'Repair') {
        payload.repair_category = form.repair_category || null;
        payload.work_description = form.work_description || '';
        payload.parts_used = form.parts_used || '';
        payload.parts_line_items = cleanLineItems;
        payload.labour_cost = parseFloat(form.labour_cost) || 0;
        payload.parts_cost = partsCostFromItems;
      } else if (form.type === 'Servicing') {
        payload.service_type = form.service_type || null;
        payload.items_changed = form.items_changed || [];
        payload.parts_line_items = cleanLineItems;
        payload.parts_cost = partsCostFromItems;
        payload.labour_cost = parseFloat(form.labour_cost) || 0;
        payload.total_cost = parseFloat(form.total_cost || (partsCostFromItems + (parseFloat(form.labour_cost) || 0))) || 0;
        payload.amount = payload.total_cost;
      } else if (form.type === 'Insurance') {
        payload.insurance_mode = form.insurance_mode || null;
        if (form.insurance_mode === 'Cash') {
          payload.parts_used = form.parts_used || '';
          payload.parts_line_items = cleanLineItems;
          payload.parts_cost = partsCostFromItems;
          payload.labour_cost = parseFloat(form.labour_cost) || 0;
          payload.total_cost = parseFloat(form.total_cost || (partsCostFromItems + (parseFloat(form.labour_cost) || 0))) || 0;
        } else if (form.insurance_mode === 'Claim') {
          payload.insurance_company = form.insurance_company || '';
          payload.claim_number = form.claim_number || '';
          payload.claim_status = form.claim_status || '';
          payload.approved_amount = parseFloat(form.approved_amount) || 0;
          payload.garage_name = form.garage_name || '';
        }
      }
      await axios.patch(`${API}/rm-records/${form.id}`, payload);

      if (billFile) {
        const reader = new FileReader();
        const dataUrl = await new Promise((res, rej) => {
          reader.onload = () => res(reader.result);
          reader.onerror = rej;
          reader.readAsDataURL(billFile);
        });
        await axios.post(`${API}/rm-records/${form.id}/upload-bill`, {
          file_name: billFile.name, file_data: dataUrl, document_type: 'bill',
        });
      }
      toast.success('Saved');
      closeForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this R&M record? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/rm-records/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadPdf = async (rec) => {
    try {
      const res = await axios.get(`${API}/rm-records/${rec.id}/report.pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rec.rnm_id || rec.rm_record_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error('Could not download report');
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/rm-records/export/excel`, { params, responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rm_records_${todayStr()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="rm-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rm-module">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-rose-600" /> R&amp;M
          </h1>
          <p className="text-sm text-slate-500">Repair & Maintenance workflow, spend and analytics.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportExcel} disabled={exporting} data-testid="rm-export-excel">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Export Excel
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="rm-new-btn">
            <Plus className="w-4 h-4 mr-2" /> New RNM
          </Button>
        </div>
      </div>

      {/* Alerts strip */}
      {alerts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="rm-alerts">
          <CardContent className="p-3 flex items-center gap-3">
            <Bell className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <div className="flex-1 text-sm text-amber-900">
              <span className="font-semibold">{alerts.length} alert{alerts.length > 1 ? 's' : ''}:</span>{' '}
              {alerts.slice(0, 3).map(a => `${a.car_number} — ${a.message}`).join('  ·  ')}
              {alerts.length > 3 && ` … +${alerts.length - 3} more`}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="rm-tabs">
          <TabsTrigger value="dashboard" data-testid="rm-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="current" data-testid="rm-tab-current">Current RNMs</TabsTrigger>
        </TabsList>

        {/* -------- DASHBOARD -------- */}
        <TabsContent value="dashboard" className="space-y-6 pt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard tone="slate"   icon={ClipboardCheck} label="Total RNMs" value={summary.total} testid="stat-total" />
            <StatCard tone="amber"   icon={Clock}          label="Active RNMs" value={summary.active}
                      sub={`${summary.pending} pending · ${summary.in_progress} in progress`} testid="stat-active" />
            <StatCard tone="emerald" icon={CheckCircle2}   label="Completed" value={summary.completed} testid="stat-completed" />
            <StatCard tone="blue"    icon={IndianRupee}    label="Total Spend" value={fmtInr(summary.total_spend)} testid="stat-spend" />
            <StatCard tone="violet"  icon={TrendingUp}     label="Avg Downtime" value={`${summary.avg_downtime_days} d`}
                      sub="days per completed RNM" testid="stat-downtime" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard tone="rose" icon={Wrench}         label="Repairs" value={summary.by_type?.Repair || 0}   testid="stat-repairs" />
            <StatCard tone="sky"  icon={Wrench}         label="Servicing" value={summary.by_type?.Servicing || 0} testid="stat-servicing" />
            <StatCard tone="violet" icon={Wrench}       label="Insurance" value={summary.by_type?.Insurance || 0} testid="stat-insurance" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="oldest-pending-card">
              <CardHeader className="pb-2"><CardTitle className="text-base">Oldest Pending RNM</CardTitle></CardHeader>
              <CardContent>
                {summary.oldest_pending ? (
                  <div className="text-sm">
                    <p className="font-mono font-semibold">{summary.oldest_pending.rnm_id}</p>
                    <p className="text-slate-600 mt-1">
                      {summary.oldest_pending.car_number} · Entered {fmtDate(summary.oldest_pending.date_of_entry)}
                    </p>
                    <StatusBadge status={summary.oldest_pending.status} />
                  </div>
                ) : <p className="text-sm text-slate-500">No pending records.</p>}
              </CardContent>
            </Card>

            <Card data-testid="cars-in-workshop-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  Cars in Workshop <span className="text-slate-500 font-normal">({summary.cars_in_workshop_count})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary.cars_in_workshop.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {summary.cars_in_workshop.map(c => (
                      <Badge key={c} className="bg-slate-100 text-slate-700 border-slate-200 font-mono">{c}</Badge>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-500">All vehicles operational.</p>}
              </CardContent>
            </Card>
          </div>

          <Card data-testid="analytics-card">
            <CardHeader className="pb-2"><CardTitle className="text-base">Cost & Frequency per Vehicle</CardTitle></CardHeader>
            <CardContent>
              {analytics.vehicles?.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Car</TableHead>
                        <TableHead className="text-right">RNMs</TableHead>
                        <TableHead className="text-right">Repairs</TableHead>
                        <TableHead className="text-right">Servicing</TableHead>
                        <TableHead className="text-right">Insurance</TableHead>
                        <TableHead className="text-right">Spend</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.vehicles.map(v => (
                        <TableRow key={v.car_number} data-testid={`ana-row-${v.car_number}`}>
                          <TableCell className="font-mono">{v.car_number}</TableCell>
                          <TableCell className="text-right">{v.count}</TableCell>
                          <TableCell className="text-right">{v.repairs}</TableCell>
                          <TableCell className="text-right">{v.servicing}</TableCell>
                          <TableCell className="text-right">{v.insurance}</TableCell>
                          <TableCell className="text-right font-semibold">{fmtInr(v.spend)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : <p className="text-sm text-slate-500">No RNM data yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- CURRENT RNMs -------- */}
        <TabsContent value="current" className="space-y-4 pt-4">
          <Card>
            <CardContent className="p-4 flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-slate-500">Car search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                  <Input
                    placeholder="Car number"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8"
                    data-testid="rm-search"
                  />
                </div>
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500">Type</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger data-testid="rm-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {RNM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="rm-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="rm-date-from" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="rm-date-to" />
              </div>
              <Button variant="outline" onClick={() => {
                setSearch(''); setFilterType('all'); setFilterStatus('all'); setDateFrom(''); setDateTo('');
              }} data-testid="rm-clear-filters">Clear</Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>RNM ID</TableHead>
                    <TableHead>Car Number</TableHead>
                    <TableHead>Date of Entry</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Workshop</TableHead>
                    <TableHead className="text-right">KM</TableHead>
                    <TableHead className="text-right w-40">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8">No records match your filters.</TableCell></TableRow>
                  )}
                  {records.map(r => (
                    <TableRow key={r.id} data-testid={`rm-row-${r.rnm_id || r.rm_record_id}`}>
                      <TableCell className="font-mono font-semibold">{r.rnm_id || r.rm_record_id}</TableCell>
                      <TableCell className="font-mono">{r.car_number}</TableCell>
                      <TableCell>{fmtDate(r.date_of_entry)}</TableCell>
                      <TableCell><TypeBadge type={r.type} /></TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell className="text-sm text-slate-700">{r.workshop_name || '—'}</TableCell>
                      <TableCell className="text-right font-mono">{r.km_reading ? r.km_reading.toLocaleString('en-IN') : '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => openForm(r)} data-testid={`rm-open-${r.rnm_id || r.rm_record_id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Open Form
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} title="Download Report" data-testid={`rm-pdf-${r.rnm_id || r.rm_record_id}`}>
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} title="Delete" data-testid={`rm-delete-${r.rnm_id || r.rm_record_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ CREATE dialog ============ */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="rm-create-dialog">
          <DialogHeader>
            <DialogTitle>New RNM</DialogTitle>
            <DialogDescription>Create a fresh Repair & Maintenance record. You can fill full details in the next step.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Car Number *</Label>
              <Input
                list="rm-veh-list"
                value={createForm.car_number}
                onChange={(e) => setCreateForm({ ...createForm, car_number: e.target.value })}
                placeholder="e.g. MH99AB1234"
                data-testid="rm-create-car"
              />
              <datalist id="rm-veh-list">
                {vehicleOptions.map(v => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={createForm.type} onValueChange={(v) => setCreateForm({ ...createForm, type: v })}>
                  <SelectTrigger data-testid="rm-create-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {RNM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>KM Reading *</Label>
                <Input type="number" min="0" value={createForm.km_reading}
                       onChange={(e) => setCreateForm({ ...createForm, km_reading: e.target.value })}
                       data-testid="rm-create-km" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Workshop Name</Label>
                <Input value={createForm.workshop_name}
                       onChange={(e) => setCreateForm({ ...createForm, workshop_name: e.target.value })} />
              </div>
              <div>
                <Label>Date of Entry</Label>
                <Input type="date" value={createForm.date_of_entry}
                       onChange={(e) => setCreateForm({ ...createForm, date_of_entry: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={submitting} data-testid="rm-create-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ FORM dialog (Details / Costing / Documents) ============ */}
      <Dialog open={formOpen} onOpenChange={(o) => o ? setFormOpen(true) : closeForm()}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="rm-form-dialog">
          {form && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      <span className="font-mono">{form.rnm_id || form.rm_record_id}</span>
                      <TypeBadge type={form.type} />
                      <StatusBadge status={form.status} />
                    </DialogTitle>
                    <DialogDescription>
                      {form.car_number} · Entered {fmtDate(form.date_of_entry)}
                    </DialogDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(form)} data-testid="rm-form-download">
                    <Download className="w-3.5 h-3.5 mr-1" /> Report
                  </Button>
                </div>
              </DialogHeader>

              <Tabs value="jobcard" className="mt-2">
                <TabsList className="hidden" />
                <TabsContent value="jobcard" className="space-y-5 pt-1" data-testid="rm-job-card">

                  {/* ========= SECTION 1 · CAR DETAILS ========= */}
                  <section className="rounded-lg border bg-slate-50/50">
                    <header className="px-4 py-2 border-b bg-slate-100 rounded-t-lg">
                      <h3 className="text-sm font-semibold text-slate-700">1 · Car Details</h3>
                    </header>
                    <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">RNM ID</Label>
                        <Input value={form.rnm_id || form.rm_record_id} readOnly disabled />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Car Number</Label>
                        <Input value={form.car_number} readOnly disabled />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Date of Entry</Label>
                        <Input type="date" value={form.date_of_entry || ''} disabled />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">KM Reading *</Label>
                        <Input type="number" min="0" value={form.km_reading ?? ''}
                               onChange={(e) => setForm({ ...form, km_reading: e.target.value })}
                               data-testid="rm-form-km" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Status</Label>
                        <Select value={form.status || 'Pending'} onValueChange={(v) => setForm({ ...form, status: v })}>
                          <SelectTrigger data-testid="rm-form-status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Completion Date</Label>
                        <Input type="date" value={form.completion_date || ''}
                               onChange={(e) => setForm({ ...form, completion_date: e.target.value })} />
                      </div>
                    </div>
                  </section>

                  {/* ========= SECTION 2 · WORK TO BE DONE ========= */}
                  <section className="rounded-lg border bg-slate-50/50" data-testid="rm-section-work">
                    <header className="px-4 py-2 border-b bg-slate-100 rounded-t-lg">
                      <h3 className="text-sm font-semibold text-slate-700">2 · Type of Work to be Done</h3>
                    </header>
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">RNM Type</Label>
                          <Select value={form.type || ''} onValueChange={(v) => setForm({ ...form, type: v })}>
                            <SelectTrigger data-testid="rm-form-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>
                              {RNM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        {form.type === 'Repair' && (
                          <div>
                            <Label className="text-xs text-slate-500">Repair Category</Label>
                            <Select value={form.repair_category || ''}
                                    onValueChange={(v) => setForm({ ...form, repair_category: v })}>
                              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                              <SelectContent>
                                {REPAIR_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {form.type === 'Servicing' && (
                          <div>
                            <Label className="text-xs text-slate-500">Service Type</Label>
                            <Select value={form.service_type || ''}
                                    onValueChange={(v) => setForm({ ...form, service_type: v })}>
                              <SelectTrigger><SelectValue placeholder="Regular / Major" /></SelectTrigger>
                              <SelectContent>
                                {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {form.type === 'Insurance' && (
                          <div>
                            <Label className="text-xs text-slate-500">Mode</Label>
                            <Select value={form.insurance_mode || ''}
                                    onValueChange={(v) => setForm({ ...form, insurance_mode: v })}>
                              <SelectTrigger><SelectValue placeholder="Cash / Claim" /></SelectTrigger>
                              <SelectContent>
                                {INSURANCE_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>

                      {form.type === 'Repair' && (
                        <div>
                          <Label className="text-xs text-slate-500">Work Description</Label>
                          <Textarea rows={3} value={form.work_description || ''}
                                    onChange={(e) => setForm({ ...form, work_description: e.target.value })}
                                    placeholder="Describe the work to be done…" />
                        </div>
                      )}

                      {form.type === 'Servicing' && (
                        <div>
                          <Label className="text-xs text-slate-500">Items Changed</Label>
                          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-1">
                            {SERVICE_ITEMS.map(item => {
                              const checked = (form.items_changed || []).includes(item);
                              return (
                                <label key={item} className="flex items-center gap-2 text-sm">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(c) => {
                                      const cur = new Set(form.items_changed || []);
                                      if (c) cur.add(item); else cur.delete(item);
                                      setForm({ ...form, items_changed: Array.from(cur) });
                                    }}
                                    data-testid={`rm-svc-item-${item.replace(/\s/g, '-').toLowerCase()}`}
                                  />
                                  {item}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {form.type === 'Insurance' && form.insurance_mode === 'Claim' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-xs text-slate-500">Insurance Company</Label>
                            <Input value={form.insurance_company || ''} onChange={(e) => setForm({ ...form, insurance_company: e.target.value })} /></div>
                          <div><Label className="text-xs text-slate-500">Claim Number</Label>
                            <Input value={form.claim_number || ''} onChange={(e) => setForm({ ...form, claim_number: e.target.value })} /></div>
                          <div><Label className="text-xs text-slate-500">Claim Status</Label>
                            <Input value={form.claim_status || ''} onChange={(e) => setForm({ ...form, claim_status: e.target.value })} /></div>
                          <div><Label className="text-xs text-slate-500">Approved Amount (₹)</Label>
                            <Input type="number" min="0" value={form.approved_amount ?? ''}
                                   onChange={(e) => setForm({ ...form, approved_amount: e.target.value })} /></div>
                        </div>
                      )}

                      <div>
                        <Label className="text-xs text-slate-500">Notes / Remarks</Label>
                        <Textarea rows={2} value={form.notes || ''}
                                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                      </div>
                    </div>
                  </section>

                  {/* ========= SECTION 3 · PLACE / WORKSHOP ========= */}
                  <section className="rounded-lg border bg-slate-50/50" data-testid="rm-section-place">
                    <header className="px-4 py-2 border-b bg-slate-100 rounded-t-lg">
                      <h3 className="text-sm font-semibold text-slate-700">3 · Place</h3>
                    </header>
                    <div className="p-4 grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Workshop Name</Label>
                        <Input list="rm-workshop-list" value={form.workshop_name || ''}
                               onChange={(e) => setForm({ ...form, workshop_name: e.target.value })}
                               placeholder="e.g. ABC Motors, Ghatkopar" />
                      </div>
                      {form.type === 'Insurance' && form.insurance_mode === 'Claim' && (
                        <div>
                          <Label className="text-xs text-slate-500">Garage Name</Label>
                          <Input value={form.garage_name || ''}
                                 onChange={(e) => setForm({ ...form, garage_name: e.target.value })} />
                        </div>
                      )}
                    </div>
                  </section>

                  {/* ========= SECTION 4 · PARTS & COSTING ========= */}
                  {(form.type === 'Repair' ||
                    form.type === 'Servicing' ||
                    (form.type === 'Insurance' && form.insurance_mode === 'Cash')) && (
                    <section className="rounded-lg border bg-slate-50/50" data-testid="rm-section-parts">
                      <header className="px-4 py-2 border-b bg-slate-100 rounded-t-lg flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-700">4 · Parts & Costing</h3>
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => setForm({
                            ...form,
                            parts_line_items: [
                              ...(form.parts_line_items || []),
                              { part_number: '', name: '', quantity: 1, unit_cost: 0 },
                            ],
                          })}
                          data-testid="rm-add-part-row"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Part
                        </Button>
                      </header>
                      <div className="p-4 space-y-3">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-40">Part No.</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="w-20 text-right">Qty</TableHead>
                                <TableHead className="w-32 text-right">Unit Cost (₹)</TableHead>
                                <TableHead className="w-32 text-right">Amount</TableHead>
                                <TableHead className="w-10"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(form.parts_line_items || []).length === 0 && (
                                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-4">
                                  No parts added. Click "Add Part" above.
                                </TableCell></TableRow>
                              )}
                              {(form.parts_line_items || []).map((it, idx) => {
                                const amt = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0);
                                const updateItem = (patch) => {
                                  const next = [...(form.parts_line_items || [])];
                                  next[idx] = { ...next[idx], ...patch };
                                  setForm({ ...form, parts_line_items: next });
                                };
                                const removeItem = () => {
                                  const next = (form.parts_line_items || []).filter((_, i) => i !== idx);
                                  setForm({ ...form, parts_line_items: next });
                                };
                                return (
                                  <TableRow key={idx} data-testid={`rm-part-row-${idx}`}>
                                    <TableCell>
                                      <Input value={it.part_number || ''}
                                             onChange={(e) => updateItem({ part_number: e.target.value })}
                                             placeholder="OEM/SKU"
                                             data-testid={`rm-part-${idx}-pn`} />
                                    </TableCell>
                                    <TableCell>
                                      <Input value={it.name || ''}
                                             onChange={(e) => updateItem({ name: e.target.value })}
                                             placeholder="e.g. Timing belt"
                                             data-testid={`rm-part-${idx}-name`} />
                                    </TableCell>
                                    <TableCell>
                                      <Input type="number" min="0" step="0.01" className="text-right"
                                             value={it.quantity ?? ''}
                                             onChange={(e) => updateItem({ quantity: e.target.value })}
                                             data-testid={`rm-part-${idx}-qty`} />
                                    </TableCell>
                                    <TableCell>
                                      <Input type="number" min="0" step="0.01" className="text-right"
                                             value={it.unit_cost ?? ''}
                                             onChange={(e) => updateItem({ unit_cost: e.target.value })}
                                             data-testid={`rm-part-${idx}-unit`} />
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-sm">
                                      {fmtInr(amt)}
                                    </TableCell>
                                    <TableCell>
                                      <Button type="button" size="icon" variant="ghost" onClick={removeItem}
                                              data-testid={`rm-part-${idx}-remove`}>
                                        <Trash2 className="w-4 h-4 text-rose-600" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Costing summary */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t">
                          <div>
                            <Label className="text-xs text-slate-500">Labour Cost (₹)</Label>
                            <Input type="number" min="0" value={form.labour_cost ?? ''}
                                   onChange={(e) => setForm({ ...form, labour_cost: e.target.value })}
                                   data-testid="rm-form-labour" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Parts Cost (auto)</Label>
                            <Input readOnly disabled value={fmtInr(
                              (form.parts_line_items || []).reduce(
                                (s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0)), 0
                              )
                            )} data-testid="rm-form-parts-cost" />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs text-slate-500">Total Cost (auto)</Label>
                            <Input readOnly disabled value={fmtInr(
                              (parseFloat(form.labour_cost) || 0) +
                              (form.parts_line_items || []).reduce(
                                (s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0)), 0
                              )
                            )} className="font-bold" data-testid="rm-form-total-cost" />
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ========= SECTION 5 · DOCUMENTS ========= */}
                  <section className="rounded-lg border bg-slate-50/50" data-testid="rm-section-documents">
                    <header className="px-4 py-2 border-b bg-slate-100 rounded-t-lg">
                      <h3 className="text-sm font-semibold text-slate-700">5 · Documents (Optional)</h3>
                    </header>
                    <div className="p-4 space-y-2">
                      <Label className="text-xs text-slate-500">Upload Bill (PDF / Image)</Label>
                      <Input type="file" accept=".pdf,image/*"
                             onChange={(e) => setBillFile(e.target.files?.[0] || null)}
                             data-testid="rm-form-bill-file" />
                      {form.bill_file_url && (
                        <a href={form.bill_file_url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                           data-testid="rm-form-existing-bill">
                          <Eye className="w-3.5 h-3.5" /> View current bill
                        </a>
                      )}
                    </div>
                  </section>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                <Button variant="outline" onClick={closeForm}>Cancel</Button>
                <Button onClick={handleSave} disabled={submitting} data-testid="rm-form-save">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RepairMaintenanceModule;
